import React, {Component, useMemo, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {
    ColumnMenuModule,
    ColumnsToolPanelModule,
    ContextMenuModule,
    MasterDetailModule,
    SetFilterModule,
} from "ag-grid-enterprise";
import {AllCommunityModule, ClientSideRowModelModule} from "ag-grid-community";
import {Helmet} from "react-helmet";
import {ClearRefresh} from "../../components/clearRefreshButtons";
import CustomNameCellEditor from "../../utils/customCellEditor";
import DTPicker, {dateFilterParametersInHeaderSuppressSorting} from "../../utils/DTPicker";
import {
    loadDataWithSSEAndStartChangeStreamListener,
    standardHandleInsertEvent,
    standardHandlePopulateGrid,
    standardHandleUpdateAndReplaceEvent
} from "../../utils/sseAndChangeStreamHelper";
import privatePageHeaderHelper from "../../utils/privatePageHeaderHelper";
import {
    getColumnModeInSession,
    getDefaultAgGridSidebarProps,
    getUseColumnStateInSession,
    onColumnStateChangedHelper,
    onGridReadyHelperForColumnState,
    updateColumnModeInSessionHelper,
    updateUseColumnStateHelper
} from "../../utils/gridFilterStateAndColumnStateHelper";
import {
    ClickToShowColumnOptionsWithToggleButtonGroup,
    customColumnModeText,
    minColumnModeText,
    standardApplyMinimumOrMediumColumnMode
} from "../../components/clickToShowButtons";
import {GridColumnFilterStateSaving} from "../../components/columnfilterComponent";
import {
    MuiAutocompleteForZenGroupsWithoutCreateGroupOption,
    MuiAutocompleteNonGroupOptions,
    MuiCloseIconButton,
    MuiIconButtonWithTooltipAndBox
} from "../../components/muiComponents";
import {dateValueFormatter} from "../../utils/gridDateFormatter";
import {Button, ThemeProvider} from "@mui/material";
import {buttonTheme} from "../../utils/muiStyling";
import Modal from "react-modal";
import NotificationManager from "react-notifications/lib/NotificationManager";
import {useForm} from "react-hook-form";
import {ccCreateFederatedIdentityProvider, listAllGroupsCCOnly} from "../api/ccOnlyAddToGroupApi";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import {DisplayOptions} from "../../components/displayOptionsComponent";
import {useOutletContext} from "react-router-dom";

let gridColumnStateSessionVariableName = "ccFederatedIdentityProvidersGridColumnState" //Not saved in user, just in session/local storage
let minColumnIds = ["fqdn", "providerName","zenGroupDisplayName","dateCreated"]

export default function CcOnlyFederatedIdentityProviders() {
    const { register, handleSubmit, reset } = useForm();
    const { isLoading, setIsLoading } = useOutletContext();
    const [gridApi, setGridApi] = useState(null);
    const [sseDataPullActive, setSSEDataPullActive] = useState(true);
    const [asyncTransactionWaitMillis, setAsyncTransactionWaitMillis] = useState(200); //200 to start for the initial sse data pull, will change when sse data pull is done for change streams
    const [enableButtons, setEnableButtons] = useState(false);
    const [useColumnStateSettingToggled, setUseColumnStateSettingToggled] = useState(getUseColumnStateInSession(gridColumnStateSessionVariableName));
    const [columnMode, setColumnMode] = useState(getColumnModeInSession(gridColumnStateSessionVariableName, minColumnModeText));
    const [finishSetupModalIsOpen, setFinishSetupModalIsOpen] = useState(false);
    const [allGroupsList, setAllGroupsList] = useState([]);
    const [zenGroupSelected, setZenGroupSelected] = useState();
    const [providerTypeSelected, setProviderTypeSelected] = useState("SAML");
    const [columnDefs, setColumnDefs] = useState([
        {
            field: "fqdn", headerName: `FQDN`, sortable: true, width: 400,
            filter: 'agSetColumnFilter',
            filterParams: {
                suppressSorting: false,
                buttons: ["reset", "apply", "cancel"],
            }
        },
        {
            field: "providerName", headerName: `AWS Cognito Provider Name`, sortable: true, width: 300,
            filter: 'agSetColumnFilter',
            filterParams: {
                suppressSorting: false,
                buttons: ["reset", "apply", "cancel"],
            }
        },
        {
            field: "providerType", headerName: `Provider Type`, sortable: true, width: 200,
            filter: 'agSetColumnFilter',
            filterParams: {
                suppressSorting: false,
                buttons: ["reset", "apply", "cancel"],
            }
        },
        {
            field: "zenGroupDisplayName", headerName: `Initial Group to Add New Users to`, sortable: true, width: 450,
            filter: 'agSetColumnFilter',
            filterParams: {
                suppressSorting: false,
                buttons: ["reset", "apply", "cancel"],
            }
        },
        { field: "dateCreated", headerName: "Date Created", width: 300,
            filter: 'agDateColumnFilter',
            filterParams:dateFilterParametersInHeaderSuppressSorting,
            sortable: true,
            valueFormatter: dateValueFormatter
        },
        {
            field: "metadataDocumentEndpointURL", headerName: `Metadata Document Endpoint URL`, sortable: true, width: 400,
            filter: 'agSetColumnFilter',
            filterParams: {
                suppressSorting: false,
                buttons: ["reset", "apply", "cancel"],
            }
        },
    ])
    const [defaultColDef, setDefaultColDef] = useState(
        {
            resizable: true,
            filterParams: null,
            floatingFilter: true,
            headerClass: "border-0 border-b-0",
            cellClass: "outline:none",
            enableCellChangeFlash: true,
            autoHeight: false,
            cellDataType: false //disable inferring cell data type automatically, can be overridden in individual colDef
        }
    )
    const sideBar = useMemo(() => {
        //Inside useMemo to help prevent the sidebar from re-rendering
        return getDefaultAgGridSidebarProps(400)
    }, []);
    const rowSelection = useMemo(() => {
        return {
            mode: 'singleRow',
            enableClickSelection: true,
            checkboxes: false,
            headerCheckbox: false
        };
    }, []);

    const submitFinishFedIDPSetup = (data) => {
        if(!data){
            NotificationManager.error("Unexpected error making this request");
            return;
        }
        if(zenGroupSelected === null || zenGroupSelected === undefined){
            NotificationManager.info("You must select the group new users will be added to if an account is made automatically for them on their first sign in")
            return;
        }
        if(data.fqdn === null || data.fqdn === undefined || data.fqdn.trim().length < 1){
            NotificationManager.info("You must enter the FQDN")
            return;
        }
        if(data.providerName === null || data.providerName === undefined || data.providerName.trim().length < 1){
            NotificationManager.info("You must enter the Identity Provider Name")
            return;
        }
        if(data.metadataDocumentEndpointURL === null || data.metadataDocumentEndpointURL === undefined || data.metadataDocumentEndpointURL.trim().length < 1){
            NotificationManager.info("You must enter the Metadata Document Endpoint URL")
            return;
        }
        if(providerTypeSelected === null || providerTypeSelected === undefined){
            NotificationManager.info("You must select the Provider Type for this integration")
            return;
        }
        setIsLoading(true)
        ccCreateFederatedIdentityProvider(zenGroupSelected, data.fqdn, data.providerName, data.metadataDocumentEndpointURL, providerTypeSelected).then(result => {
            setIsLoading(false)
            NotificationManager.success("Successfully created the new federated identity provider");
            resetModalFields()
        }).catch(function (error) {
            setIsLoading(false)
            if (error.message){
                NotificationManager.error(error.message);
            }
            else{
                NotificationManager.error("Unexpected error making this request");
            }
        })
    };


    function resetModalFields(){
        setFinishSetupModalIsOpen(false)
        setZenGroupSelected()
        setProviderTypeSelected("SAML")
        reset({
            fqdn: "",
            providerName: "",
            metadataDocumentEndpointURL: "",
        })
    }

    return (
        <div className="flex flex-col h-full">
            <Helmet>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>Federated Identity Providers</title>
                <script src="https://js.stripe.com/v3/"/>
                <link href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap" rel="stylesheet"/>
            </Helmet>
            <Modal contentLabel="Finish Federated IDP Setup For SSO Integration with Client"
                   isOpen={finishSetupModalIsOpen}
                   onRequestClose={() => {
                       resetModalFields()
                   }}
                   shouldCloseOnOverlayClick={true}
                   className={`focus:outline-none focus:shadow-sm border-2 flex relative z-50 bg-white w-2xl max-w-2xl inset-y-10 mx-auto rounded-2xl`}
                   overlayClassName="z-50 bg-black bg-opacity-5 fixed inset-0 overflow-scroll"
            >
                <form className="flex flex-1 flex-col p-8 w-full ml-4 mr-4" onSubmit={handleSubmit(submitFinishFedIDPSetup)}>
                    <div className="flex flex-1 flex-col gap-y-4">
                        {/*Title with exit button*/}
                        <div className="flex flex-row justify-between">
                            <h1 className="font-bold text-3xl">Finish Federated IDP Setup For SSO Integration with Client</h1>
                            <MuiCloseIconButton
                                onClick={() => {
                                    resetModalFields()
                                }}
                            />
                        </div>
                        <hr className="mt-3 h-0.5" />
                        {/*Form content*/}
                        <div>
                            This should be the last step in the internal integration guide found <a className={"underline text-blue-600"} rel="noreferrer" target="_blank" href={"https://cybercrucible.atlassian.net/wiki/x/CoBoIw"}>here</a>. The FQDN and
                            Provider Name fields are the most important and integrations will not work if these fields are not entered exactly how they are in AWS!
                        </div>
                        <hr className="h-0.5" />
                        <div className="">
                            <label>Select which group this Federated IDP is for. New users will be added to this group automatically when signing in with this SSO integration for the first time.
                                Also, the group selected here will hold the association used for the group enforce SSO Login setting</label>
                            <MuiAutocompleteForZenGroupsWithoutCreateGroupOption
                                zenGroupDropdownOptionsList={allGroupsList}
                                value={zenGroupSelected}
                                onChange={( event, value ) => {
                                    setZenGroupSelected(value?.value)
                                }}
                            />
                        </div>
                        <div className={"flex flex-row w-full gap-x-10 justify-between flex-nowrap items-end"}>
                            <div className={`w-full`}>
                                <label>The FQDN entered in the AWS Route 53 CNAME record. Copy and Paste exactly</label>
                                <input
                                    type="text"
                                    name="fqdn" {...register("fqdn")}
                                    required
                                    className="focus:outline-none h-10 p-2 w-full mt-3 rounded-lg border border-black border-opacity-25 border-solid"
                                    placeholder={'black.panther.dashboard.cybercrucible.com'}
                                />
                            </div>
                        </div>
                        <div className={"flex flex-row w-full gap-x-10 justify-between flex-nowrap items-end"}>
                            <div className={`w-full`}>
                                <label>The Federated identity provider name/id entered into our AWS Cognito User Pool. Copy and Paste exactly</label>
                                <input
                                    type="text"
                                    name="providerName" {...register("providerName")}
                                    required
                                    className="focus:outline-none h-10 p-2 w-full mt-3 rounded-lg border border-black border-opacity-25 border-solid"
                                    placeholder={'CyberCrucibleEntraID'}
                                />
                            </div>
                        </div>
                        <div className={"flex flex-row w-full gap-x-10 justify-between flex-nowrap items-end"}>
                            <div className={`w-full`}>
                                <label>The Federated identity provider Metadata document endpoint URL</label>
                                <input
                                    type="text"
                                    name="metadataDocumentEndpointURL" {...register("metadataDocumentEndpointURL")}
                                    required
                                    className="focus:outline-none h-10 p-2 w-full mt-3 rounded-lg border border-black border-opacity-25 border-solid"
                                    placeholder={'https://login.microsoftonline.com/89fdc494...'}
                                />
                            </div>
                        </div>
                        <div className={"flex flex-row w-full gap-x-10 justify-between flex-nowrap items-end"}>
                            <div className={`w-full`}>
                                <label>Provider Type - Currently We Only Support SAML with Microsoft Entra ID</label>
                                <MuiAutocompleteNonGroupOptions
                                    options={["SAML"]}
                                    value={providerTypeSelected}
                                    onChange={(event, value) => {
                                        setProviderTypeSelected(value)
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col mt-5">
                            <ThemeProvider theme = {buttonTheme}>
                                <Button type={"submit"} color={"primary"} variant={"contained"}>
                                    Submit
                                </Button>
                            </ThemeProvider>
                        </div>
                    </div>
                </form>
            </Modal>
            <div className="flex flex-1 flex-col flex-nowrap gap-y-3 h-full">
                    {privatePageHeaderHelper("Federated Identity Providers")}
                    <hr className="h-0.5" />
                    <DisplayOptions useColumnStateSettingToggled={useColumnStateSettingToggled}
                                    setUseColumnStateSettingToggled={setUseColumnStateSettingToggled}
                                    toggleUpdateUseColumnState={toggleUpdateUseColumnState} />
                    <div className="flex flex-row justify-between gap-x-1 gap-y-3">
                        <div className={"self-end flex flex-col gap-y-3"}>
                            <div className="flex flex-row justify-start gap-x-6 flex-wrap gap-y-2 items-center">
                                <MuiIconButtonWithTooltipAndBox
                                    icon={<PersonAddAlt1Icon className={"cursor-pointer"}/>} tooltipTitle={"Finish Federated IDP Setup For SSO Integration with Client"}
                                    tooltipPlacement={"top"}
                                    onClick={() => {
                                        resetModalFields()
                                        setFinishSetupModalIsOpen(true)
                                        //if we have not populated allGroupsList yet then do so now
                                        if(allGroupsList.length === 0) {
                                            listAllGroupsCCOnly().then(response => {
                                                let groupsFormattedForOptions = []
                                                response.forEach(group => {
                                                    let objToPush = {}
                                                    objToPush.value = group.id
                                                    objToPush.label = group.userSetFriendlyName || group.friendlyName || group.id
                                                    groupsFormattedForOptions.push(objToPush)
                                                })
                                                groupsFormattedForOptions.sort(function (a, b) {
                                                    return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
                                                });
                                                setAllGroupsList(groupsFormattedForOptions)
                                            }).catch(error => {})
                                        }
                                    }}/>
                            </div>
                        </div>
                        <div className={"flex flex-row flex-wrap gap-y-3 gap-x-8 self-end justify-end"}>
                            <ClickToShowColumnOptionsWithToggleButtonGroup
                                columnMode={columnMode} setColumnMode={setColumnMode} gridColumnStateSessionVariableName={gridColumnStateSessionVariableName} gridApi={gridApi}
                                minColumnIds={minColumnIds} disableMediumOption={true} updateGridColumnModeFunction={null} />
                            <ClearRefresh gridApi = {gridApi} showRefreshIcon={false} />
                        </div>
                    </div>
                    <div className="h-full flex flex-col gap-y-5" id="gridRoot">
                        <Grid
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            sideBar={sideBar}
                            rowSelection={rowSelection}
                            setGridApi={setGridApi}
                            sseDataPullActive={sseDataPullActive}
                            setEnableButtons={setEnableButtons}
                            setSSEDataPullActive={setSSEDataPullActive}
                            asyncTransactionWaitMillis={asyncTransactionWaitMillis}
                            setAsyncTransactionWaitMillis={setAsyncTransactionWaitMillis}
                            columnMode={columnMode}
                            setColumnMode={setColumnMode}
                        />
                    </div>
                </div>
        </div>
    );
    function toggleUpdateUseColumnState(toggleSetting){
        updateUseColumnStateHelper(toggleSetting, gridColumnStateSessionVariableName, null);
    }
}

class Grid extends Component {
    rowData = []
    updateTransactionsToApply = []
    abortController = new AbortController()

    constructor(props, onClickRow, filterVals) {
        super(props);
    }

    onCellEditingStopped = (event) => {
        let gridApi = event.api
        //Nothing to edit on this grid
    }

    componentWillUnmount(){
        this.abortController.abort()
    }

    populateGrid = async (rowData) => {
        standardHandlePopulateGrid(rowData, this.gridApi)
    }

    updateGridForChangeStream = async (changeStreamData) => {
        let operationType = changeStreamData.operationType
        let objectBody = changeStreamData.body
        if(operationType === "UPDATE" || operationType === "REPLACE"){
            standardHandleUpdateAndReplaceEvent(objectBody, this.gridApi, this.props.sseDataPullActive, this.updateTransactionsToApply)
        }
        else if (operationType === "INSERT"){
            standardHandleInsertEvent(objectBody, this.gridApi, this.props.sseDataPullActive)
        }
    }

    getRowId = (params) => {
        return params.data.fqdn
    }

    getContextMenuItems = (params) => {
        return [
            "resetColumns",
            "autoSizeAll"
        ];
    };

    onColumnStateChanged = (params) => {
        //function to handle when column state changes: sort change, column visibility changes, or a column position on grid is moved
        if(params.source !== "api" && params.source !== "gridOptionsChanged"){
            this.props.setColumnMode && this.props.setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, null)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, null, customColumnModeText)
        }
        else if(params.source === "api" && params.type === "sortChanged"){
            this.props.setColumnMode && this.props.setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, null)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, null, customColumnModeText)
        }

    }

    onGridReady = async (gridReadyParams) => {
        this.gridApi = gridReadyParams.api;
        this.props.setGridApi(gridReadyParams.api);

        // Disable text selection on the page while holding shift or control (to allow grid selections to be done easily without selecting all text)
        ["keyup","keydown"].forEach((event) => {
            window.addEventListener(event, (e) => {
                document.onselectstart = function() {
                    return !(e.shiftKey || e.ctrlKey);
                }
            });
        });

        let columnMode = this.props.columnMode
        //check which initial column mode to apply
        if(columnMode === customColumnModeText){
            onGridReadyHelperForColumnState(gridReadyParams, gridColumnStateSessionVariableName)
        }
        else if(columnMode === minColumnModeText){
            standardApplyMinimumOrMediumColumnMode(gridColumnStateSessionVariableName, gridReadyParams.api, this.props.setColumnMode, minColumnModeText, minColumnIds, null)
        }
        //Medium not allowed for this grid, only min/max/custom
        //else if columnMode is max then the default column state already shows the max amount of columns no need to update


        await loadDataWithSSEAndStartChangeStreamListener("/ccRetrieveAllFederatedIdentityProviders", null,
            this.populateGrid, this.updateGridForChangeStream, gridReadyParams, this.props.setSSEDataPullActive, this.props.setAsyncTransactionWaitMillis, this.updateTransactionsToApply,
            this.abortController)
    };
    render() {
        return (
            <div className={"w-full h-full"} style={{minHeight: "400px"}}>
                <div id="myGrid" className="ag-theme-alpine rounded-md shadow h-full w-full">
                    <AgGridReact
                        columnMenu={"legacy"}
                        modules={[
                            ClientSideRowModelModule,
                            AllCommunityModule,
                            ColumnMenuModule,
                            ContextMenuModule,
                            ColumnsToolPanelModule,
                            SetFilterModule,
                            MasterDetailModule
                        ]}
                        defaultColDef={this.props.defaultColDef}
                        columnDefs={this.props.columnDefs}
                        components={{agDateInput: DTPicker, customNameCellEditor: CustomNameCellEditor}}
                        multiSortKey={"ctrl"}
                        rowData={this.rowData}
                        onGridReady={this.onGridReady}
                        asyncTransactionWaitMillis={this.props.asyncTransactionWaitMillis}
                        suppressModelUpdateAfterUpdateTransaction={true}
                        getRowId={this.getRowId}
                        maintainColumnOrder={true}
                        onCellEditingStopped={this.onCellEditingStopped}
                        rowSelection={this.props.rowSelection}
                        onSelectionChanged={() => {
                            const selectedRows = this.gridApi.getSelectedRows();
                            if(selectedRows && selectedRows.length > 0){
                                //checks if the setEnableButtons method is null or not
                                this.props.setEnableButtons && this.props.setEnableButtons(true);
                            }
                            else{
                                this.props.setEnableButtons && this.props.setEnableButtons(false);
                            }
                        }}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        getContextMenuItems={this.getContextMenuItems}
                        sideBar={this.props.sideBar}
                        onSortChanged={this.onColumnStateChanged}
                        onColumnMoved={this.onColumnStateChanged}
                        onColumnVisible={this.onColumnStateChanged}
                    />
                </div>
            </div>
        );
    }
}
